/**
 * Element-Bibliothek Pro - Canva-Level Element-Auswahl
 *
 * Features:
 * - 9.000+ Phosphor Icons in 6 Stilen
 * - 20+ Foto-Rahmen
 * - 15+ Layout-Vorlagen
 * - Stock Videos (Pexels)
 * - 50+ Illustrationen (unDraw-Style)
 * - Lottie-Animationen
 * - Blob/Wave Generator
 * - Device Mockups
 * - Diagramme (Charts)
 * - Editierbare Tabellen
 * - Audio-Bibliothek
 * - Lazy Loading für Performance
 * - Integrierter Color Picker
 * - Favoriten & Zuletzt verwendet
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  PHOSPHOR_CATEGORIES,
  getIconsByCategory,
  searchPhosphorIcons,
  getPhosphorIcon,
  getIconDisplayName,
  getPhosphorIconCount,
  WEIGHT_LABELS,
  type IconWeight,
} from '../config/phosphorIcons';
import {
  ELEMENT_CATEGORIES,
  LibraryElement,
  searchElements,
  getAllElements,
} from '../config/elementLibrary';
import {
  ELEMENT_TABS,
  type ElementTabType,
  type FrameElement,
  type LayoutElement,
  type IllustrationElement,
  type AnimationElement,
  type MockupElement,
  type AudioElement,
} from '../config/elementTypes';
import { FRAME_LIBRARY, searchFrames, getFramesByCategory } from '../config/frameLibrary';
import { LAYOUT_LIBRARY, searchLayouts } from '../config/layoutLibrary';
import { getAllIllustrations, searchIllustrations, recolorIllustration } from '../config/illustrationLibrary';
import { getAllAnimations, searchAnimations } from '../config/lottieLibrary';
import { generateBlobSvg, BLOB_PRESETS, type BlobConfig } from '../utils/blobGenerator';
import { MOCKUP_LIBRARY, searchMockups, getMockupsByCategory } from '../config/mockupLibrary';
import { CHART_TEMPLATES, searchCharts } from '../config/chartTemplates';
import { TABLE_STYLES, type ExtendedTableElement } from '../config/tableStyles';
import { AUDIO_LIBRARY, searchAudio, getAudioByType, formatDuration } from '../config/audioLibrary';
import { LottieThumbnail } from './LottiePreview';
import type { ChartElement } from '../config/elementTypes';

// Storage keys
const FAVORITES_KEY = 'elementLibrary_favorites';
const RECENT_KEY = 'elementLibrary_recent';
const MAX_RECENT = 20;
const ITEMS_PER_PAGE = 60;

// Union type for all library items
type LibraryItem =
  | { type: 'icon'; id: string; name: string; iconName: string }
  | { type: 'shape'; id: string; name: string; element: LibraryElement }
  | { type: 'frame'; id: string; name: string; frame: FrameElement }
  | { type: 'layout'; id: string; name: string; layout: LayoutElement }
  | { type: 'illustration'; id: string; name: string; illustration: IllustrationElement }
  | { type: 'animation'; id: string; name: string; animation: AnimationElement }
  | { type: 'mockup'; id: string; name: string; mockup: MockupElement }
  | { type: 'chart'; id: string; name: string; chart: ChartElement }
  | { type: 'table'; id: string; name: string; table: ExtendedTableElement }
  | { type: 'audio'; id: string; name: string; audio: AudioElement };

interface ElementLibraryProProps {
  onSelectElement: (element: LibraryElement & {
    phosphorIcon?: string;
    phosphorWeight?: IconWeight;
    frameData?: FrameElement;
    layoutData?: LayoutElement;
    illustrationData?: IllustrationElement;
    animationData?: AnimationElement;
    blobConfig?: BlobConfig;
    mockupData?: MockupElement;
    chartData?: ChartElement;
    tableData?: ExtendedTableElement;
    audioData?: AudioElement;
  }) => void;
  isOpen: boolean;
  onClose: () => void;
  initialColor?: string;
}

// Load from localStorage
function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
}

// Save to localStorage
function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
}

// Color Picker Mini Component
function ColorPickerMini({
  color,
  onChange,
}: {
  color: string;
  onChange: (color: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const presetColors = [
    '#000000', '#FFFFFF', '#6B7280', '#EF4444', '#F97316', '#F59E0B',
    '#22C55E', '#10B981', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899', '#F43F5E',
  ];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const popupHeight = 200;

      setPosition({
        top: spaceBelow < popupHeight ? rect.top - popupHeight - 8 : rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
      >
        <div
          className="w-6 h-6 rounded-md border border-gray-300"
          style={{ backgroundColor: color }}
        />
        <span className="text-sm text-gray-700">{color}</span>
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={popupRef}
          className="fixed z-[100] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-64"
          style={{ top: position.top, left: position.left }}
        >
          <div className="grid grid-cols-5 gap-2 mb-3">
            {presetColors.map((c) => (
              <button
                key={c}
                onClick={() => {
                  onChange(c);
                  setIsOpen(false);
                }}
                className={`w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110 ${
                  color === c ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => onChange(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                  onChange(e.target.value);
                }
              }}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded font-mono"
              placeholder="#000000"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// Phosphor Icon Renderer Component
function PhosphorIconRenderer({
  name,
  weight,
  color,
  size,
}: {
  name: string;
  weight: IconWeight;
  color: string;
  size: number;
}) {
  const IconComponent = getPhosphorIcon(name);

  if (!IconComponent) {
    return (
      <div className="flex items-center justify-center text-gray-300" style={{ width: size, height: size }}>
        ?
      </div>
    );
  }

  return <IconComponent size={size} weight={weight} color={color} />;
}

// Generic Item Card
function ItemCard({
  children,
  name,
  isFavorite,
  onSelect,
  onToggleFavorite,
  badge,
}: {
  children: React.ReactNode;
  name: string;
  isFavorite: boolean;
  onSelect: () => void;
  onToggleFavorite: () => void;
  badge?: string;
}) {
  return (
    <button
      onClick={onSelect}
      className="relative aspect-square bg-white border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:shadow-lg transition-all group overflow-hidden"
      title={name}
    >
      <div className="w-full h-full flex items-center justify-center p-3">
        {children}
      </div>

      {/* Badge */}
      {badge && (
        <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded">
          {badge}
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-2">
        <span className="text-white text-xs font-medium px-2 text-center truncate max-w-full">
          {name}
        </span>
      </div>

      {/* Favorite button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite();
        }}
        className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
          isFavorite
            ? 'bg-yellow-400 text-yellow-900 opacity-100'
            : 'bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-yellow-500'
        }`}
      >
        <svg className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      </button>

      {/* Add button */}
      <div className="absolute top-1 left-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
        </svg>
      </div>
    </button>
  );
}

// Blob Generator Panel
function BlobGeneratorPanel({
  color,
  onSelect,
}: {
  color: string;
  onSelect: (svg: string, config: BlobConfig) => void;
}) {
  const { t } = useTranslation();
  const [complexity, setComplexity] = useState(6);
  const [contrast, setContrast] = useState(0.5);
  const [seed, setSeed] = useState(Math.random() * 10000);

  const config: BlobConfig = { complexity, contrast, seed, size: 100 };
  const blobSvg = generateBlobSvg(config, color);

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-center">
        <div
          className="w-40 h-40 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onSelect(blobSvg, config)}
          dangerouslySetInnerHTML={{ __html: blobSvg }}
        />
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">{t('creator:library.complexity')}: {complexity}</label>
          <input
            type="range"
            min={3}
            max={10}
            value={complexity}
            onChange={(e) => setComplexity(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">{t('creator:library.contrast')}: {(contrast * 100).toFixed(0)}%</label>
          <input
            type="range"
            min={0}
            max={100}
            value={contrast * 100}
            onChange={(e) => setContrast(Number(e.target.value) / 100)}
            className="w-full"
          />
        </div>
        <button
          onClick={() => setSeed(Math.random() * 10000)}
          className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          {t('creator:library.generateNew')}
        </button>
      </div>

      <div className="border-t pt-4">
        <p className="text-xs text-gray-500 mb-2">{t('creator:library.presets')}:</p>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(BLOB_PRESETS).map(([name, preset]) => (
            <button
              key={name}
              onClick={() => {
                setComplexity(preset.complexity);
                setContrast(preset.contrast);
                setSeed(Math.random() * 10000);
              }}
              className="p-2 text-xs bg-gray-100 rounded hover:bg-gray-200 transition-colors capitalize"
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ElementLibraryPro({
  onSelectElement,
  isOpen,
  onClose,
  initialColor = '#3B82F6',
}: ElementLibraryProProps) {
  const { t } = useTranslation();
  // State
  const [activeTab, setActiveTab] = useState<ElementTabType>('icons');
  const [selectedCategory, setSelectedCategory] = useState<string>('popular');
  const [iconWeight, setIconWeight] = useState<IconWeight>('regular');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewColor, setPreviewColor] = useState(initialColor);
  const [favorites, setFavorites] = useState<string[]>(() => loadFromStorage(FAVORITES_KEY, []));
  const [recent, setRecent] = useState<string[]>(() => loadFromStorage(RECENT_KEY, []));
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset on tab/category change
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
    setSearchQuery('');
    setSelectedCategory(activeTab === 'icons' ? 'popular' : 'all');
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Get categories for current tab
  const categories = useMemo(() => {
    switch (activeTab) {
      case 'icons':
        return PHOSPHOR_CATEGORIES;
      case 'shapes':
        return ELEMENT_CATEGORIES.map(c => ({ id: c.id, name: c.name, icon: c.icon }));
      case 'frames':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '🖼️' },
          { id: 'polaroid', name: 'Polaroid', icon: '📷' },
          { id: 'circle', name: t('creator:library.circles'), icon: '⭕' },
          { id: 'vintage', name: t('creator:library.vintage'), icon: '🎞️' },
          { id: 'modern', name: t('creator:library.modern'), icon: '▢' },
          { id: 'decorative', name: t('creator:library.decorative'), icon: '✨' },
          { id: 'minimal', name: t('creator:library.minimal'), icon: '─' },
        ];
      case 'layouts':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '⊞' },
          { id: 'columns', name: t('creator:library.columns'), icon: '▥' },
          { id: 'hero', name: t('creator:library.hero'), icon: '▣' },
          { id: 'masonry', name: t('creator:library.masonry'), icon: '⊟' },
          { id: 'sidebar', name: t('creator:library.sidebar'), icon: '⊏' },
        ];
      case 'illustrations':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '🎭' },
          { id: 'business', name: t('creator:library.business'), icon: '💼' },
          { id: 'technology', name: t('creator:library.technology'), icon: '💻' },
          { id: 'people', name: t('creator:library.people'), icon: '👤' },
          { id: 'nature', name: t('creator:library.nature'), icon: '🌿' },
          { id: 'abstract', name: t('creator:library.abstract'), icon: '🔮' },
          { id: 'social', name: t('creator:library.socialMedia'), icon: '📱' },
        ];
      case 'animations':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '✨' },
          { id: 'social', name: t('creator:library.socialIcons'), icon: '❤️' },
          { id: 'loading', name: t('creator:library.loading'), icon: '⏳' },
          { id: 'success', name: t('creator:library.success'), icon: '✅' },
          { id: 'decorative', name: t('creator:library.decorative'), icon: '🌟' },
        ];
      case 'mockups':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '📱' },
          { id: 'phone', name: t('creator:library.smartphones'), icon: '📱' },
          { id: 'tablet', name: t('creator:library.tablets'), icon: '📋' },
          { id: 'laptop', name: t('creator:library.laptops'), icon: '💻' },
          { id: 'desktop', name: t('creator:library.desktop'), icon: '🖥️' },
          { id: 'apparel', name: t('creator:library.products'), icon: '👕' },
        ];
      case 'charts':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '📊' },
          { id: 'bar', name: t('creator:library.bar'), icon: '📊' },
          { id: 'line', name: t('creator:library.line'), icon: '📈' },
          { id: 'pie', name: t('creator:library.pie'), icon: '🥧' },
        ];
      case 'tables':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '📋' },
        ];
      case 'audio':
        return [
          { id: 'all', name: t('creator:library.allCategories'), icon: '🎵' },
          { id: 'music', name: t('creator:library.music'), icon: '🎶' },
          { id: 'sfx', name: t('creator:library.soundFx'), icon: '🔊' },
          { id: 'ambient', name: t('creator:library.ambient'), icon: '🌊' },
        ];
      default:
        return [{ id: 'all', name: 'Alle', icon: '📁' }];
    }
  }, [activeTab]);

  // Get displayed items
  const allItems = useMemo((): LibraryItem[] => {
    const query = searchQuery.trim().toLowerCase();

    switch (activeTab) {
      case 'icons': {
        let icons: string[];
        if (query) {
          icons = searchPhosphorIcons(query).slice(0, 1000);
        } else if (selectedCategory === 'favorites') {
          icons = favorites.filter(id => id.startsWith('phosphor-')).map(id => id.replace('phosphor-', ''));
        } else if (selectedCategory === 'recent') {
          icons = recent.filter(id => id.startsWith('phosphor-')).map(id => id.replace('phosphor-', ''));
        } else {
          icons = getIconsByCategory(selectedCategory);
        }
        return icons.map(name => ({
          type: 'icon' as const,
          id: `phosphor-${name}`,
          name: getIconDisplayName(name),
          iconName: name,
        }));
      }

      case 'shapes': {
        let elements: LibraryElement[];
        if (query) {
          elements = searchElements(query);
        } else if (selectedCategory === 'favorites') {
          elements = getAllElements().filter(el => favorites.includes(el.id));
        } else if (selectedCategory === 'recent') {
          elements = recent.map(id => getAllElements().find(el => el.id === id)).filter((el): el is LibraryElement => !!el);
        } else {
          const cat = ELEMENT_CATEGORIES.find(c => c.id === selectedCategory);
          elements = cat?.elements || getAllElements();
        }
        return elements.map(el => ({
          type: 'shape' as const,
          id: el.id,
          name: el.name,
          element: el,
        }));
      }

      case 'frames': {
        let frames: FrameElement[];
        if (query) {
          frames = searchFrames(query);
        } else if (selectedCategory === 'all') {
          frames = FRAME_LIBRARY;
        } else {
          frames = getFramesByCategory(selectedCategory as FrameElement['frameStyle']);
        }
        return frames.map((f: FrameElement) => ({
          type: 'frame' as const,
          id: f.id,
          name: f.name,
          frame: f,
        }));
      }

      case 'layouts': {
        let layouts: LayoutElement[] = query ? searchLayouts(query) : LAYOUT_LIBRARY;
        if (selectedCategory !== 'all' && !query) {
          layouts = layouts.filter((l: LayoutElement) => l.gridType === selectedCategory);
        }
        return layouts.map((l: LayoutElement) => ({
          type: 'layout' as const,
          id: l.id,
          name: l.name,
          layout: l,
        }));
      }

      case 'illustrations': {
        let illustrations: IllustrationElement[] = query ? searchIllustrations(query) : getAllIllustrations();
        if (selectedCategory !== 'all' && !query) {
          illustrations = illustrations.filter((i: IllustrationElement) => i.category === selectedCategory);
        }
        return illustrations.map((i: IllustrationElement) => ({
          type: 'illustration' as const,
          id: i.id,
          name: i.name,
          illustration: i,
        }));
      }

      case 'animations': {
        let animations: AnimationElement[] = query ? searchAnimations(query) : getAllAnimations();
        if (selectedCategory !== 'all' && !query) {
          animations = animations.filter((a: AnimationElement) => a.category === selectedCategory);
        }
        return animations.map((a: AnimationElement) => ({
          type: 'animation' as const,
          id: a.id,
          name: a.name,
          animation: a,
        }));
      }

      case 'mockups': {
        let mockups: MockupElement[];
        if (query) {
          mockups = searchMockups(query);
        } else if (selectedCategory === 'all') {
          mockups = MOCKUP_LIBRARY;
        } else {
          mockups = getMockupsByCategory(selectedCategory as MockupElement['deviceType']);
        }
        return mockups.map(m => ({
          type: 'mockup' as const,
          id: m.id,
          name: m.name,
          mockup: m,
        }));
      }

      case 'charts': {
        let charts: ChartElement[] = query ? searchCharts(query) : CHART_TEMPLATES;
        if (selectedCategory !== 'all' && !query) {
          charts = charts.filter((c: ChartElement) => c.chartType === selectedCategory);
        }
        return charts.map((c: ChartElement) => ({
          type: 'chart' as const,
          id: c.id,
          name: c.name,
          chart: c,
        }));
      }

      case 'tables': {
        return TABLE_STYLES.map((t: ExtendedTableElement) => ({
          type: 'table' as const,
          id: t.id,
          name: t.name,
          table: t,
        }));
      }

      case 'audio': {
        let audio: AudioElement[];
        if (query) {
          audio = searchAudio(query);
        } else if (selectedCategory === 'all') {
          audio = AUDIO_LIBRARY;
        } else {
          audio = getAudioByType(selectedCategory as AudioElement['audioType']);
        }
        return audio.map((a: AudioElement) => ({
          type: 'audio' as const,
          id: a.id,
          name: a.name,
          audio: a,
        }));
      }

      default:
        return [];
    }
  }, [activeTab, selectedCategory, searchQuery, favorites, recent]);

  const displayedItems = useMemo((): LibraryItem[] => allItems.slice(0, visibleCount), [allItems, visibleCount]);

  // Scroll handler
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const bottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (bottom < 200 && visibleCount < allItems.length) {
      setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, allItems.length));
    }
  }, [visibleCount, allItems.length]);

  // Selection handler
  const handleSelect = useCallback((item: LibraryItem) => {
    const baseElement: LibraryElement = {
      id: item.id,
      name: item.name,
      svg: '',
      tags: [],
      defaultWidth: 100,
      defaultHeight: 100,
      defaultFill: previewColor,
    };

    // Add to recent
    const newRecent = [item.id, ...recent.filter(id => id !== item.id)].slice(0, MAX_RECENT);
    setRecent(newRecent);
    saveToStorage(RECENT_KEY, newRecent);

    // Type-specific handling
    if (item.type === 'icon' && 'iconName' in item) {
      onSelectElement({
        ...baseElement,
        phosphorIcon: item.iconName,
        phosphorWeight: iconWeight,
      });
    } else if (item.type === 'shape' && 'element' in item) {
      onSelectElement({ ...item.element, defaultFill: previewColor });
    } else if (item.type === 'frame' && 'frame' in item) {
      onSelectElement({
        ...baseElement,
        svg: item.frame.svg,
        frameData: item.frame,
      });
    } else if (item.type === 'layout' && 'layout' in item) {
      onSelectElement({
        ...baseElement,
        svg: item.layout.thumbnail,
        layoutData: item.layout,
      });
    } else if (item.type === 'illustration' && 'illustration' in item) {
      onSelectElement({
        ...baseElement,
        svg: recolorIllustration(item.illustration.svg, previewColor),
        illustrationData: item.illustration,
      });
    } else if (item.type === 'animation' && 'animation' in item) {
      onSelectElement({
        ...baseElement,
        animationData: item.animation,
      });
    } else if (item.type === 'mockup' && 'mockup' in item) {
      onSelectElement({
        ...baseElement,
        svg: item.mockup.svg,
        mockupData: item.mockup,
      });
    } else if (item.type === 'chart' && 'chart' in item) {
      onSelectElement({
        ...baseElement,
        chartData: item.chart,
        defaultWidth: 200,
        defaultHeight: 150,
      });
    } else if (item.type === 'table' && 'table' in item) {
      onSelectElement({
        ...baseElement,
        tableData: item.table,
        defaultWidth: 200,
        defaultHeight: 120,
      });
    } else if (item.type === 'audio' && 'audio' in item) {
      onSelectElement({
        ...baseElement,
        audioData: item.audio,
      });
    }

    onClose();
  }, [previewColor, iconWeight, recent, onSelectElement, onClose]);

  // Toggle favorite
  const toggleFavorite = useCallback((id: string) => {
    const newFavorites = favorites.includes(id)
      ? favorites.filter(f => f !== id)
      : [...favorites, id];
    setFavorites(newFavorites);
    saveToStorage(FAVORITES_KEY, newFavorites);
  }, [favorites]);

  // Handle blob selection
  const handleBlobSelect = useCallback((svg: string, config: BlobConfig) => {
    const element: LibraryElement = {
      id: `blob-${Date.now()}`,
      name: 'Blob',
      svg,
      tags: ['blob', 'organic'],
      defaultWidth: 100,
      defaultHeight: 100,
      defaultFill: previewColor,
    };
    onSelectElement({ ...element, blobConfig: config });
    onClose();
  }, [previewColor, onSelectElement, onClose]);

  if (!isOpen) return null;

  // Tab counts
  const tabCounts: Record<ElementTabType, string> = {
    icons: `${getPhosphorIconCount().toLocaleString()}+`,
    shapes: String(getAllElements().length),
    frames: String(FRAME_LIBRARY.length),
    layouts: String(LAYOUT_LIBRARY.length),
    videos: 'Pexels',
    illustrations: String(getAllIllustrations().length),
    animations: String(getAllAnimations().length),
    blobs: '∞',
    mockups: String(MOCKUP_LIBRARY.length),
    charts: String(CHART_TEMPLATES.length),
    tables: String(TABLE_STYLES.length),
    audio: String(AUDIO_LIBRARY.length),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-[1100px] max-w-[95vw] h-[800px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('creator:library.title')}</h2>
            <p className="text-sm text-gray-500">
              {ELEMENT_TABS.find(t => t.id === activeTab)?.description || 'Wähle Elemente für dein Design'}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <ColorPickerMini color={previewColor} onChange={setPreviewColor} />
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex overflow-x-auto px-4 py-2 gap-1 scrollbar-hide">
            {ELEMENT_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span className={`text-xs ${activeTab === tab.id ? 'text-blue-100' : 'text-gray-400'}`}>
                  {tabCounts[tab.id]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Categories */}
          {activeTab !== 'blobs' && activeTab !== 'videos' && (
            <div className="w-48 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
              {/* Favorites & Recent */}
              <div className="p-3 border-b border-gray-200 space-y-1">
                <button
                  onClick={() => setSelectedCategory('favorites')}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === 'favorites' ? 'bg-yellow-100 text-yellow-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>⭐</span>
                  <span>Favoriten</span>
                </button>
                <button
                  onClick={() => setSelectedCategory('recent')}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === 'recent' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>🕐</span>
                  <span>Zuletzt</span>
                </button>
              </div>

              {/* Category list */}
              <div className="flex-1 overflow-y-auto p-3">
                <div className="space-y-1">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        setSearchQuery('');
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm font-medium transition-colors ${
                        selectedCategory === cat.id && !searchQuery
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span>{cat.icon}</span>
                      <span className="truncate">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search & Filters */}
            {activeTab !== 'blobs' && (
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-4">
                <div className="flex-1 relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`${ELEMENT_TABS.find(t => t.id === activeTab)?.label} suchen...`}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Icon Weight Selector */}
                {activeTab === 'icons' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Stil:</span>
                    <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                      {(['thin', 'light', 'regular', 'bold', 'fill', 'duotone'] as IconWeight[]).map(weight => (
                        <button
                          key={weight}
                          onClick={() => setIconWeight(weight)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            iconWeight === weight
                              ? 'bg-white text-blue-600 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {WEIGHT_LABELS[weight]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Results count */}
            {searchQuery && activeTab !== 'blobs' && (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-sm text-gray-500">
                  {allItems.length} Ergebnis{allItems.length !== 1 ? 'se' : ''} für "{searchQuery}"
                </p>
              </div>
            )}

            {/* Content Area */}
            {activeTab === 'blobs' ? (
              <BlobGeneratorPanel color={previewColor} onSelect={handleBlobSelect} />
            ) : activeTab === 'videos' ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <span className="text-6xl mb-4 block">🎬</span>
                  <p className="text-lg mb-2">Pexels Stock Videos</p>
                  <p className="text-sm">Öffne die Medienbibliothek für Video-Suche</p>
                </div>
              </div>
            ) : (
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4" onScroll={handleScroll}>
                {displayedItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Keine Elemente gefunden</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                      {displayedItems.map(item => (
                        <ItemCard
                          key={item.id}
                          name={item.name}
                          isFavorite={favorites.includes(item.id)}
                          onSelect={() => handleSelect(item)}
                          onToggleFavorite={() => toggleFavorite(item.id)}
                          badge={
                            item.type === 'audio' && 'audio' in item
                              ? formatDuration(item.audio.duration)
                              : item.type === 'animation'
                              ? '✨'
                              : undefined
                          }
                        >
                          {/* Render based on type */}
                          {item.type === 'icon' && 'iconName' in item && (
                            <PhosphorIconRenderer
                              name={item.iconName}
                              weight={iconWeight}
                              color={previewColor}
                              size={48}
                            />
                          )}
                          {item.type === 'shape' && 'element' in item && (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ color: previewColor }}
                              dangerouslySetInnerHTML={{
                                __html: item.element.svg.replace(/currentColor/g, previewColor),
                              }}
                            />
                          )}
                          {item.type === 'frame' && 'frame' in item && (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ color: previewColor }}
                              dangerouslySetInnerHTML={{
                                __html: item.frame.svg.replace(/currentColor/g, previewColor),
                              }}
                            />
                          )}
                          {item.type === 'layout' && 'layout' in item && (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              dangerouslySetInnerHTML={{ __html: item.layout.thumbnail }}
                            />
                          )}
                          {item.type === 'illustration' && 'illustration' in item && (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              dangerouslySetInnerHTML={{
                                __html: recolorIllustration(item.illustration.svg, previewColor),
                              }}
                            />
                          )}
                          {item.type === 'animation' && 'animation' in item && (
                            <LottieThumbnail
                              animationData={item.animation.animationData}
                              size={56}
                            />
                          )}
                          {item.type === 'mockup' && 'mockup' in item && (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              dangerouslySetInnerHTML={{ __html: item.mockup.svg }}
                            />
                          )}
                          {item.type === 'chart' && 'chart' in item && (
                            <div className="w-full h-full flex items-center justify-center text-3xl">
                              {item.chart.chartType === 'bar' && '📊'}
                              {item.chart.chartType === 'line' && '📈'}
                              {item.chart.chartType === 'pie' && '🥧'}
                              {item.chart.chartType === 'donut' && '🍩'}
                              {item.chart.chartType === 'area' && '📉'}
                            </div>
                          )}
                          {item.type === 'table' && 'table' in item && (
                            <div className="w-full h-full flex items-center justify-center text-3xl">📋</div>
                          )}
                          {item.type === 'audio' && 'audio' in item && (
                            <div className="w-full h-full flex items-center justify-center text-3xl">
                              {item.audio.audioType === 'music' && '🎵'}
                              {item.audio.audioType === 'sfx' && '🔊'}
                              {item.audio.audioType === 'ambient' && '🌊'}
                            </div>
                          )}
                        </ItemCard>
                      ))}
                    </div>

                    {visibleCount < allItems.length && (
                      <div className="mt-4 text-center text-sm text-gray-400">
                        {displayedItems.length} von {allItems.length} geladen - Scrolle für mehr
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Klicke auf ein Element, um es hinzuzufügen. ⭐ zum Favorisieren.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ElementLibraryPro;
